/* eslint-disable react/prefer-stateless-function */
import React from 'react';
import {fromJS} from 'immutable';

import {
  Text,
  Button,
  Modal,
  Dialogue,
  Icon,
  Card,
  Input,
  copyToClipboard,
  addToast
} from '@albert-io/atomic';

import {loadable} from 'lib/loadable';
import {callAction} from 'client/framework';
import constants from 'client/constants';
import sessionStore from 'client/Session/SessionStore';
import MarkdownRendererV2 from 'generic/MarkdownRendererV2/MarkdownRendererV2.react';
import {latexCheatSheetText} from 'generic/LatexCheatSheet/latexCheatSheetText';
import {
  getCanonicalLink,
  getEditorLink
} from 'client/SubjectPracticeView/subjectPracticeView.utils';
import abandonQueueChangesStore from 'client/Dennis/Content/Queue/AbandonQueueChanges.store';
import ConfirmLeaveModal from 'generic/ConfirmLeaveModal/ConfirmLeaveModal.react';
import SupplementManagerModalButton from 'client/Supplements/SupplementManager/SupplementManagerModalButton.react';
import StandardSearch from 'generic/StandardSearch/StandardSearch.react';
import SimpleDropdown from 'generic/SimpleDropdown/SimpleDropdown.react';
import TextInput from 'generic/Forms/TextInput/TextInput.react';

import QuestionSetAlignment from '../QuestionSetAlignment/QuestionSetAlignment.react';
import QuestionTypeManager from '../QuestionTypeManager/QuestionTypeManager.react';
import AuthoringTextInput from '../AuthoringTextInput/AuthoringTextInput.react';

import DifficultyEditor from './DifficultyEditor.react';
import questionEditorActions from './QuestionEditor.actions';
import QuestionTagEditor from './QuestionTags/QuestionTagEditor.react';
import questionEditorStore from './QuestionEditor.store';

import './question-editor.scss';

const MarkdownCheatSheet = loadable(() =>
  import('generic/MarkdownCheatSheet/MarkdownCheatSheet.react')
);

const statusOptions = fromJS([
  {
    id: constants.QUESTIONSET_STATUSES.REJECTED,
    name: 'Rejected',
    status: constants.QUESTIONSET_STATUSES.REJECTED
  },
  {
    id: constants.QUESTIONSET_STATUSES.DRAFT_MODE,
    name: 'Draft mode',
    status: constants.QUESTIONSET_STATUSES.DRAFT_MODE
  },
  {
    id: constants.QUESTIONSET_STATUSES.IN_REVIEW,
    name: 'In review',
    status: constants.QUESTIONSET_STATUSES.IN_REVIEW
  },
  {
    id: constants.QUESTIONSET_STATUSES.HIDDEN,
    name: 'Hidden',
    status: constants.QUESTIONSET_STATUSES.HIDDEN
  },
  {
    id: constants.QUESTIONSET_STATUSES.PUBLISHED,
    name: 'Published',
    status: constants.QUESTIONSET_STATUSES.PUBLISHED
  },
  {
    id: constants.QUESTIONSET_STATUSES.ARCHIVED,
    name: 'Archived',
    status: constants.QUESTIONSET_STATUSES.ARCHIVED
  }
]);

const subTypeOptions = fromJS([
  {
    id: 'free_response_question',
    name: 'FRQ'
  },
  {
    id: 'document_based_question',
    name: 'DBQ'
  },
  {
    id: 'classroom_sample_task',
    name: 'CST'
  },
  {
    id: 'short_answer',
    name: 'Short Answer'
  },
  {
    id: 'long_answer',
    name: 'Long Answer'
  }
]);

export default class QuestionEditor extends React.Component {
  constructor() {
    super();
    this.state = {
      showLatexModal: false,
      showMarkdownModal: false
    };
  }

  toggleLatexModal = () => {
    this.setState((state) => ({
      showLatexModal: !state.showLatexModal
    }));
  };

  toggleMarkdownModal = () => {
    this.setState((state) => ({
      showMarkdownModal: !state.showMarkdownModal
    }));
  };

  handleStatusChange = (option) => {
    callAction(questionEditorActions.UPDATE_QUESTION_SET_STATUS, {
      status: option.get('status')
    });
  };

  handleSubTypeChange = (option) => {
    callAction(questionEditorActions.UPDATE_QUESTION_DETAILS, {
      setter: 'setQuestionSubType',
      value: option.get('id')
    });
  };

  updateQuestionDetails({setter, value, validate}) {
    callAction(questionEditorActions.UPDATE_QUESTION_DETAILS, {setter, value});
    callAction(questionEditorActions.VALIDATE_QUESTION_FIELD, validate);
  }

  handleChange = (e) => {
    const element = e.target;
    this.updateQuestionDetails({
      setter: element.dataset.setterMethod,
      validate: element.dataset.validate,
      value: element.value
    });
  };

  handleAuthoringTextInputChange = (e) => {
    this.updateQuestionDetails({
      setter: e.target.dataset.setterMethod,
      validate: e.target.dataset.validate,
      value: e.target.value
    });
  };

  handleTagChange = (tag, remove = false) => {
    callAction(questionEditorActions.UPDATE_QUESTION_SET_TAGS, {remove, tag});
  };

  /**
   * This is is one of two methods of updating the question model that is passed to the <QuestionTypeManager />.
   * This method lets the QuestionTypeManager pass the updated model and any methods it wishes to validate, and we
   * write to the store directly.
   *
   * @param {*} model Updated AuthoringQuestion model
   * @param  {...string} methodsToValidate Methods to validate as part of updating the mode
   */
  handleQuestionTypeChange = (model, ...methodsToValidate) => {
    callAction(questionEditorActions.UPDATE_QUESTION, model);
    if (methodsToValidate.length > 0) {
      methodsToValidate.forEach((method) => {
        callAction(questionEditorActions.VALIDATE_QUESTION_FIELD, method);
      });
    }
  };

  /**
   * This is is one of two methods of updating the question model that is passed to the <QuestionTypeManager />.
   * This method passes the current question to a callback that the QuestionTypeManager provides, and uses the result
   * of that callback to create a new model. This lets us create a QuestionTypeManager that does not rerender on store
   * changes.
   *
   * @param {Function} updateFunc Used by child component to create updated question
   */
  handleUpdateQuestion(updateFunc) {
    const question = questionEditorStore.getQuestion();
    const updatedQuestion = updateFunc(question);
    callAction(questionEditorActions.UPDATE_QUESTION, updatedQuestion);
  }

  generateStatusDropdown() {
    if (!sessionStore.isSuper()) {
      return null;
    }
    /**
     * @todo: Have the back-end put the current status (_not_ category) as meta,
     * and add a model validator for this. This is kinda a hack, but the back end will do the
     * validation for now. Since this dropdown is only seen by CM's, I hope this is fine for now.
     */
    const questionSet = questionEditorStore.getQuestionSet();
    const status = questionSet.getStatus();
    const isUnaligned =
      questionSet.getAuthoringExams().isEmpty() && questionSet.getAuthoringGuideLevels().isEmpty();
    const statusError =
      isUnaligned &&
      [constants.QUESTIONSET_STATUSES.IN_REVIEW, constants.QUESTIONSET_STATUSES.PUBLISHED].includes(
        status
      ) ? (
        <p className='a-form-input__error'>
          Please align the question to a guide level or assessment before moving it to &lsquo;in
          review&rsquo; or &lsquo;published&rsquo;.
        </p>
      ) : null;
    return (
      <div className='question-editor__question-detail--fullwidth'>
        <SimpleDropdown
          label='Status'
          activeItemId={status}
          options={statusOptions}
          storeName='QuestionStatusStore'
          onChange={this.handleStatusChange}
          destroyStoreOnUnmount
        />
        {statusError}
      </div>
    );
  }

  generateSubTypeDropdown() {
    if (questionEditorStore.getQuestion().getQuestionType() !== 'free-response') {
      return null;
    }

    return (
      <SimpleDropdown
        className='question-editor__question-detail--fullwidth'
        label='Question Sub-Type'
        activeItemId={questionEditorStore.getQuestion().getQuestionSubType()}
        options={subTypeOptions}
        storeName='QuestionSubTypeStore'
        onChange={this.handleSubTypeChange}
        destroyStoreOnUnmount
      />
    );
  }

  /**
   * Determines if a user should be prompted to abandon changes when leaving a route
   *
   * Takes into account the state of `abandonQueueChangesStore`, which is settable
   * via the abandon changes modal defined in the top level of the Queue.
   */
  shouldPromptLeave() {
    const currentQuestionSet = questionEditorStore.getQuestionSet();
    const currentQuestion = questionEditorStore.getQuestion();
    if (!currentQuestion) {
      return false;
    }

    const hasChanges =
      !currentQuestion.getChangeMap().isEmpty() || !currentQuestionSet.getChangeMap().isEmpty();
    if (hasChanges && abandonQueueChangesStore.hasAbandonedChanges()) {
      return false;
    }

    return hasChanges;
  }

  handleBlur = (e) => {
    const {dataset, selectionStart, selectionEnd} = e.target;
    const name = dataset.fieldPath || e.target.name;
    callAction(questionEditorActions.SAVE_CURSOR_POSITION, {
      end: selectionEnd,
      name,
      start: selectionStart
    });
  };

  render() {
    if (!questionEditorStore.isReady()) {
      return null;
    }
    const question = questionEditorStore.getQuestion();
    const questionSet = questionEditorStore.getQuestionSet();
    const errors = questionEditorStore.getValidationErrors();
    return (
      <div className='question-editor'>
        <div className='question-editor__question-edit-wrapper card'>
          <div className='question-editor__question-details'>
            {this.generateStatusDropdown()}
            {this.generateSubTypeDropdown()}
            <DifficultyEditor
              className='question-editor__question-detail--fullwidth'
              difficulty={question.getDifficulty()}
              data-validate='difficulty'
              error={Boolean(errors.get('difficulty'))}
              errorMessage={errors.get('difficulty')}
            />
            <hr />
            <TextInput
              className='question-editor__question-detail--fullwidth'
              label='Title'
              data-setter-method='setTitle'
              data-validate='title'
              name='title'
              value={question.getTitle()}
              onChange={this.handleChange}
              error={Boolean(errors.get('title'))}
              errorMessage={errors.get('title')}
            />
            <AuthoringTextInput
              label='Prompt'
              data-setter-method='setPrompt'
              data-validate='prompt'
              data-field-path='prompt'
              value={question.getPrompt()}
              error={errors.get('prompt')}
              onChange={this.handleAuthoringTextInputChange}
              onBlur={this.handleBlur}
              className='question-editor__question-detail--fullwidth question-editor__prompt'
            />
            <div className='question-editor__cheat-sheet-wrapper'>
              <Button variant='text' onClick={this.toggleMarkdownModal}>
                Markdown cheat sheet
              </Button>
              {this.state.showMarkdownModal && (
                <Modal
                  longModal
                  ariaLabel='Markdown cheat sheet'
                  handleClose={this.toggleMarkdownModal}
                >
                  {({modalContentStyle}) => {
                    return (
                      <Dialogue
                        handleClose={this.toggleMarkdownModal}
                        className={modalContentStyle}
                        size='xl'
                      >
                        <Dialogue.Body>
                          <MarkdownCheatSheet />
                        </Dialogue.Body>
                      </Dialogue>
                    );
                  }}
                </Modal>
              )}
              <Button variant='text' className='u-mar-l_1' onClick={this.toggleLatexModal}>
                LaTeX cheat sheet
              </Button>
              {this.state.showLatexModal && (
                <Modal longModal ariaLabel='Latex cheat cheet' handleClose={this.toggleLatexModal}>
                  {({modalContentStyle}) => {
                    return (
                      <Dialogue
                        handleClose={this.toggleLatexModal}
                        className={modalContentStyle}
                        size='xl'
                      >
                        <Dialogue.Body>
                          <MarkdownRendererV2 text={latexCheatSheetText} />
                        </Dialogue.Body>
                      </Dialogue>
                    );
                  }}
                </Modal>
              )}
              <Button
                className='u-mar-l_1'
                as='a'
                href='https://sites.google.com/albert.io/authorwiki/'
                target='_blank'
                variant='text'
              >
                Author Wiki <Icon className='u-mar-l_1' icon='external-link-alt' />
              </Button>
            </div>
            <SupplementManagerModalButton
              className='question-editor__add-supplement-btn'
              pathToProperty='prompt'
            />
          </div>
          <div className='question-editor__question-type'>
            <QuestionTypeManager
              question={question}
              onBlur={this.handleBlur}
              onModelChange={this.handleQuestionTypeChange}
              validationErrors={errors}
              updateQuestion={this.handleUpdateQuestion}
            />
          </div>
          <hr />
          <div className='question-editor__question-otherstuff'>
            <AuthoringTextInput
              label='Explanation of Solution'
              data-setter-method='setSolutionText'
              data-validate='solution_text'
              height='auto'
              data-field-path='solution_text'
              value={question.getSolutionText()}
              error={errors.get('solution_text')}
              onChange={this.handleAuthoringTextInputChange}
              onBlur={this.handleBlur}
              className='question-editor__question-detail--fullwidth question-editor__solution'
            />
            <SupplementManagerModalButton
              className='question-editor__add-supplement-btn'
              pathToProperty='solution_text'
            />
          </div>
          <div className='question-editor__label-tag-inputs'>
            <UrlSlug />
            <StandardSearch
              standards={question.getStandards()}
              subjectId={questionSet.getSubjectId()}
              disabled={!questionSet.getId()}
            />
            <QuestionTagEditor tags={questionSet.getTags()} onTagClick={this.handleTagChange} />
          </div>
          <QuestionSetAlignment questionSetId={questionSet.getId()} />
          <ConfirmLeaveModal
            shouldPromptFunc={this.shouldPromptLeave}
            storeName='questionEditorIERouteChangeHandler'
            modalTitle='You have unsaved changes'
            modalPrompt={
              <p>
                There are unsaved changes on this question.
                <br />
                Are you sure you want to leave?
              </p>
            }
            onLeave={this.onRouteLeave}
          />
        </div>
      </div>
    );
  }
}

class UrlSlug extends React.Component {
  render() {
    let questionLink = getCanonicalLink({
      activeQuestion: questionEditorStore.getQuestion(),
      activeQuestionSet: questionEditorStore.getQuestionSet()
    });

    const editorLink = getEditorLink({
      activeQuestion: questionEditorStore.getQuestion(),
      activeQuestionSet: questionEditorStore.getQuestionSet()
    });

    if (!questionLink || !editorLink) {
      return null;
    }

    const isPublished =
      questionEditorStore.getQuestionSet().getStatus() === constants.QUESTIONSET_STATUSES.PUBLISHED;

    if (!isPublished) {
      /**
       * When a question is unpublished `getCanonicalLink` will cast `null`
       * to a string, since it doesn't have a question slug yet. Here,
       * we're just replacing that for display.
       */
      questionLink = questionLink.replace('null', '{QUESTION SLUG}');
    }
    const fullPath = constants.ALBERT_FULL_PATH + questionLink;
    const fullEditorPath = constants.ALBERT_FULL_PATH + editorLink;
    const content = (
      <Card roundness='small' paddingLevel={3}>
        <Text as='div' bold>
          Editor Link
        </Text>
        <div className='u-display_flex u-align-items_baseline'>
          <Input
            readOnly
            border
            value={fullEditorPath}
            size='regular'
            type='text'
            className='u-mar-t_1 u-mar-r_1'
          />
          <Button
            variant='outlined'
            onClick={() =>
              copyToClipboard(fullEditorPath, () => {
                addToast({
                  type: 'success',
                  ariaLabel: 'Copied to clipboard',
                  message: 'Copied to clipboard.'
                });
              })
            }
          >
            <Icon icon='copy' className='u-mar-r_1' />
            Copy
          </Button>
        </div>
        <Text className='u-mar-tb_1' size='s' as='div'>
          Share this link with other authors or reviewers.
        </Text>
        <Text as='div' bold className='u-mar-t_5'>
          Public Link
        </Text>
        <div className='u-display_flex u-align-items_baseline'>
          <Input
            readOnly
            border
            value={fullPath}
            size='regular'
            type='text'
            className='u-mar-t_1 u-mar-r_1'
          />
          <Button
            variant='outlined'
            onClick={() =>
              copyToClipboard(fullPath, () => {
                addToast({
                  type: 'success',
                  ariaLabel: 'Copied to clipboard',
                  message: 'Copied to clipboard.'
                });
              })
            }
          >
            <Icon icon='copy' className='u-mar-r_1' />
            Copy
          </Button>
        </div>
        {isPublished ? (
          <Text className='u-mar-tb_1' size='s' as='div'>
            This link is only accessible once the question has been published.
          </Text>
        ) : (
          <Text className='u-mar-tb_1' size='s' as='div'>
            A question slug will only be assigned after publication. This link is only accessible
            once the question has been published.
          </Text>
        )}
      </Card>
    );

    return (
      <div className='u-mar-tb_1'>
        <div>{content}</div>
      </div>
    );
  }
}
